import { useState, useEffect, forwardRef } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import Papa from 'papaparse';
import { Copy, Check, Download, FileText, FileJson, FileSpreadsheet, FileCode } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import JsonTreeView from '@/components/ui/json-tree-view';
import { getSpacesPresignedUrl } from '@minohealth/common/services/SpacesService';

const DATA_EXTENSIONS = ['txt', 'json', 'xml', 'csv'];

function getExtFromUrl(url) {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    const filename = pathname.split('/').pop() || '';
    const dotIdx = filename.lastIndexOf('.');
    if (dotIdx === -1) return null;
    return filename.slice(dotIdx + 1).toLowerCase();
  } catch {
    const filename = url.split('/').pop()?.split('?')[0] || '';
    const dotIdx = filename.lastIndexOf('.');
    if (dotIdx === -1) return null;
    return filename.slice(dotIdx + 1).toLowerCase();
  }
}

export function isDataFile(url) {
  return DATA_EXTENSIONS.includes(getExtFromUrl(url));
}

export function getDataFileExt(url) {
  const ext = getExtFromUrl(url);
  return DATA_EXTENSIONS.includes(ext) ? ext : null;
}

function getFileIcon(ext) {
  switch (ext) {
    case 'json': return FileJson;
    case 'csv': return FileSpreadsheet;
    case 'xml': return FileCode;
    default: return FileText;
  }
}

// Pretty-prints XML using DOMParser. Falls back to the original string if parsing fails.
function prettyPrintXml(xml) {
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml');
    if (doc.querySelector('parsererror')) return xml;
    return serializeNode(doc.documentElement, 0).trimEnd();
  } catch {
    return xml;
  }
}

function serializeNode(node, depth) {
  const indent = '  '.repeat(depth);
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent.trim();
    return text ? indent + text : '';
  }
  if (node.nodeType === Node.COMMENT_NODE) {
    return `${indent}<!--${node.textContent}-->`;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';

  const attrs = Array.from(node.attributes)
    .map(a => ` ${a.name}="${a.value}"`)
    .join('');

  const children = Array.from(node.childNodes)
    .map(child => serializeNode(child, depth + 1))
    .filter(Boolean);

  if (children.length === 0) return `${indent}<${node.tagName}${attrs}/>`;

  // Inline short single-text-child elements
  if (children.length === 1 && !children[0].includes('\n') && children[0].length < 80) {
    return `${indent}<${node.tagName}${attrs}>${children[0].trim()}</${node.tagName}>`;
  }

  return `${indent}<${node.tagName}${attrs}>\n${children.join('\n')}\n${indent}</${node.tagName}>`;
}

// Tokenises XML text into typed spans rendered as React elements — no innerHTML needed.
function tokeniseXml(text) {
  const tokens = [];
  // Matches: XML comments, tags (opening/closing/self-closing), or plain text runs
  const re = /(<!--[\s\S]*?-->)|(<\/?[a-zA-Z][^>]*?>)|("[^"]*")|([^<"]+|[<"])/g;
  let match;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match[1]) {
      tokens.push(<span key={key++} className="text-muted-foreground italic">{match[1]}</span>);
    } else if (match[2]) {
      tokens.push(<span key={key++} className="text-blue-400 dark:text-blue-300">{match[2]}</span>);
    } else if (match[3]) {
      tokens.push(<span key={key++} className="text-green-500 dark:text-green-400">{match[3]}</span>);
    } else {
      tokens.push(match[4]);
    }
  }
  return tokens;
}

function TxtViewer({ content }) {
  return (
    <div className="overflow-auto h-full">
      <pre className="text-sm font-mono whitespace-pre-wrap wrap-break-word text-foreground leading-relaxed px-4 py-3">
        {content}
      </pre>
    </div>
  );
}

function JsonViewer({ content }) {
  let parsed;
  try {
    parsed = JSON.parse(content);
  } catch {
    return <TxtViewer content={content} />;
  }
  return (
    <div className="overflow-auto h-full px-4 py-3">
      <JsonTreeView
        data={parsed}
        defaultExpandDepth={2}
        showDataTypes={false}
        showCopyButtons={true}
      />
    </div>
  );
}

function XmlViewer({ content }) {
  const formatted = prettyPrintXml(content);
  return (
    <div className="overflow-auto h-full">
      <pre className="text-sm font-mono whitespace-pre-wrap wrap-break-word leading-relaxed px-4 py-3">
        {tokeniseXml(formatted)}
      </pre>
    </div>
  );
}

const TABLE_COMPONENTS = {
  Table: ({ style, ...props }) => (
    <table style={style} className="text-sm border-collapse w-full min-w-max" {...props} />
  ),
  TableHead: forwardRef(({ style, ...props }, ref) => (
    <thead ref={ref} style={style} className="bg-muted" {...props} />
  )),
  TableBody: forwardRef(({ style, ...props }, ref) => (
    <tbody ref={ref} style={style} {...props} />
  )),
  TableRow: ({ style, ...props }) => (
    <tr style={style} className="border-b border-border/50 hover:bg-muted/40" {...props} />
  ),
};

function CsvViewer({ content }) {
  const { data, meta, errors } = Papa.parse(content, {
    header: true,
    skipEmptyLines: true,
  });

  if (errors.length > 0 && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-destructive">Failed to parse CSV: {errors[0].message}</p>
      </div>
    );
  }

  const headers = meta.fields || [];

  return (
    <TableVirtuoso
      style={{ height: '100%' }}
      data={data}
      components={TABLE_COMPONENTS}
      fixedHeaderContent={() => (
        <tr>
          {headers.map(h => (
            <th
              key={h}
              className="text-left px-3 py-2 font-medium text-foreground border-b border-border whitespace-nowrap"
            >
              {h}
            </th>
          ))}
        </tr>
      )}
      itemContent={(_, row) => (
        <>
          {headers.map(h => (
            <td
              key={h}
              className="px-3 py-1.5 text-muted-foreground whitespace-nowrap max-w-[300px] truncate"
              title={String(row[h] ?? '')}
            >
              {String(row[h] ?? '')}
            </td>
          ))}
        </>
      )}
    />
  );
}

function ViewerContent({ ext, content }) {
  switch (ext) {
    case 'json': return <JsonViewer content={content} />;
    case 'csv': return <CsvViewer content={content} />;
    case 'xml': return <XmlViewer content={content} />;
    default: return <TxtViewer content={content} />;
  }
}

/**
 * @param {{
 *   url: string,
 *   name: string,
 *   userToken: string,
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 * }} props
 */
export function DataFileViewer({ url, name, userToken, open, onOpenChange }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const ext = getDataFileExt(url);
  const Icon = getFileIcon(ext);

  // Fetch on open, but only once per mount
  useEffect(() => {
    if (!open || !url) return;
    if (content !== null) return;

    setLoading(true);
    setError(null);

    getSpacesPresignedUrl({ objectUrl: url, userToken })
      .then(presignedUrl => fetch(presignedUrl))
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then(text => setContent(text))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, url, userToken]);

  const handleCopy = async () => {
    if (!content) return;
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = async () => {
    const presignedUrl = await getSpacesPresignedUrl({ objectUrl: url, userToken });
    const res = await fetch(presignedUrl);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl! w-full h-[85vh] flex! flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 text-base pr-6">
            <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
            <span className="truncate">{name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden min-h-0">
          {loading && (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Loading…
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center h-full text-destructive text-sm">
              {error}
            </div>
          )}
          {!loading && !error && content !== null && (
            <ViewerContent ext={ext} content={content} />
          )}
        </div>

        <div className="shrink-0 flex items-center justify-end gap-2">
          <button
            onClick={handleCopy}
            disabled={!content}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border bg-background hover:bg-muted transition-colors disabled:opacity-40"
          >
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border bg-background hover:bg-muted transition-colors"
          >
            <Download className="w-3 h-3" />
            Download
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
